import { Capacitor, registerPlugin } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

// Native print + share for the Capacitor shell. Both feature-detect, because the web
// bundle deploys on every merge while the native plugins only exist in builds that
// include them: on an older build the callers fall back to the Safari hop.
//
// Print: in-repo PrinterPlugin (ios/App/App/PrinterPlugin.swift) wrapping
// UIPrintInteractionController — iOS presents its own print sheet in-app, one tap.
// Share: the official Share + Filesystem plugins. The PDF is written to the app's
// cache and handed to the iOS share sheet, which is where "Save to Files" (iCloud
// Drive, Google Drive, On My iPhone), AirDrop, Mail and Print all live.

interface PrinterPlugin {
  printPdf(options: { base64: string; jobName?: string }): Promise<{ completed: boolean }>;
}
const Printer = registerPlugin<PrinterPlugin>("Printer");

export const hasNativePrinter = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Printer");

export const hasNativeShare = (): boolean =>
  Capacitor.isNativePlatform() &&
  Capacitor.isPluginAvailable("Share") &&
  Capacitor.isPluginAvailable("Filesystem");

async function fetchPdfBase64(url: string): Promise<string> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`PDF fetch failed (${res.status})`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(binary);
}

/** Fetch the order PDF (session cookie works in-app) and open the iOS print sheet. */
export async function printPdfNatively(pdfUrl: string, jobName: string): Promise<boolean> {
  const base64 = await fetchPdfBase64(pdfUrl);
  const { completed } = await Printer.printPdf({ base64, jobName });
  return completed;
}

/**
 * Fetch the order PDF, stage it in the app cache, and present the share sheet.
 * Resolves false when the user dismisses the sheet without choosing anything.
 */
export async function sharePdfNatively(pdfUrl: string, fileName: string, title: string): Promise<boolean> {
  const data = await fetchPdfBase64(pdfUrl);
  const { uri } = await Filesystem.writeFile({ path: fileName, data, directory: Directory.Cache });
  try {
    await Share.share({ title, files: [uri] });
    return true;
  } catch (err) {
    // The Share plugin rejects when the sheet is dismissed; that is not an error.
    if (/cancel/i.test(String((err as Error)?.message ?? err))) return false;
    throw err;
  }
}
